import { ThemeEvents } from './events.js';

const selectors = {
  close: '[data-cart-drawer-close]',
  drawer: '[data-cart-drawer]',
  open: '[data-cart-drawer-open]',
  panel: '[data-cart-drawer-panel]',
};

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

let returnFocusElement;
let closeTimer;

const drawerAnimationMs = 260;

function getCartDrawer() {
  return document.querySelector(selectors.drawer);
}

function focusableElements(root) {
  return Array.from(root.querySelectorAll(focusableSelector)).filter((element) => {
    return element.offsetParent !== null || element === document.activeElement;
  });
}

export function openCartDrawer(returnFocus = document.activeElement) {
  const drawer = getCartDrawer();
  if (!drawer) return;

  window.clearTimeout(closeTimer);
  returnFocusElement = returnFocus instanceof HTMLElement ? returnFocus : document.activeElement;
  if (drawer instanceof HTMLDialogElement && !drawer.open) {
    drawer.showModal();
  }
  drawer.classList.add('is-visible');
  drawer.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('cart-drawer-open');

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
    });
  });

  const panel = drawer.querySelector(selectors.panel);
  const focusTarget = panel?.querySelector(selectors.close) || panel?.querySelector(focusableSelector) || panel || drawer;
  focusTarget.focus();
}

export function closeCartDrawer(restoreFocus = true) {
  const drawer = getCartDrawer();
  if (!drawer) return;

  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove('cart-drawer-open');

  window.clearTimeout(closeTimer);
  closeTimer = window.setTimeout(() => {
    drawer.classList.remove('is-visible');
    if (drawer instanceof HTMLDialogElement && drawer.open) {
      drawer.close();
    }
  }, drawerAnimationMs);

  if (restoreFocus && returnFocusElement?.isConnected) {
    returnFocusElement.focus();
  }
  returnFocusElement = null;
}

function trapCartDrawerFocus(event) {
  if (event.key !== 'Tab') return;

  const drawer = getCartDrawer();
  if (!drawer?.classList.contains('is-open')) return;

  const focusables = focusableElements(drawer);
  if (focusables.length === 0) {
    event.preventDefault();
    drawer.querySelector(selectors.panel)?.focus();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const openButton = event.target.closest(selectors.open);
  if (openButton) {
    event.preventDefault();
    openCartDrawer(openButton);
    return;
  }

  if (event.target.closest(selectors.close)) {
    event.preventDefault();
    closeCartDrawer();
    return;
  }

  if (event.target.matches(selectors.drawer)) {
    closeCartDrawer();
  }
});

document.addEventListener('close', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.matches(selectors.drawer)) return;

  event.target.classList.remove('is-open');
  event.target.classList.remove('is-visible');
  event.target.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove('cart-drawer-open');
}, true);

document.addEventListener('cancel', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.matches(selectors.drawer)) return;

  event.preventDefault();
  closeCartDrawer();
}, true);

document.addEventListener('keydown', (event) => {
  trapCartDrawerFocus(event);

  if (event.key === 'Escape') {
    closeCartDrawer();
  }
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const source = event.detail?.data?.source;
  if (source === 'product-form' || source === 'cart-add') {
    window.requestAnimationFrame(() => openCartDrawer(event.detail?.data?.returnFocus));
  }
});

document.addEventListener(ThemeEvents.drawerOpen, (event) => {
  openCartDrawer(event.detail?.returnFocus);
});

document.addEventListener(ThemeEvents.drawerClose, () => {
  closeCartDrawer();
});

document.addEventListener(ThemeEvents.legacyCartDrawerOpen, (event) => {
  openCartDrawer(event.detail?.returnFocus);
});

document.addEventListener(ThemeEvents.legacyCartDrawerClose, () => {
  closeCartDrawer();
});

window.MedistikCartDrawer = {
  open: openCartDrawer,
  close: closeCartDrawer,
};
